import { useState } from 'react';
import { useApiData } from 'lib/api';
import { useAchievementTypes, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { EmptyIndicator } from 'components/common';
import { Box } from 'components/common/box';
import { ProfileSummaryTimelineItem, ProfileSummaryTimelineToggle } from '.';

import type { Achievement } from 'shared-types';
import type { GetUserTimelineRes } from 'shared-types/responses';
import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  isUserPage: boolean;
  targetUserId: number;
}

const ProfileSummaryTimeline = ({ isUserPage, targetUserId }: Props) => {
  const { messages, theme, user: loggedInUser } = useReduxState();

  const achievementTypes = useAchievementTypes();

  const [showAll, setShowAll] = useState(false);

  const { data, loading } = useApiData<GetUserTimelineRes>({
    url: `/admin/users/${targetUserId}/timeline`
  });

  const NUMBER_OF_ITEMS_LIMIT = 5;

  const totalNumberOfItems = data?.timelineItems?.length || 0;

  const timelineItems = showAll
    ? data?.timelineItems
    : data?.timelineItems?.slice(0, NUMBER_OF_ITEMS_LIMIT);

  const timelineItemsByYear: {
    timelineItems: GetUserTimelineRes['timelineItems'];
    year: number;
  }[] = [];

  timelineItems?.forEach((timelineItem) => {
    const year = new Date(timelineItem.completionDate).getFullYear();
    let index = timelineItemsByYear.length - 1;
    if (timelineItemsByYear[index]?.year !== year) {
      timelineItemsByYear.push({ year, timelineItems: [] });
      index++;
    }
    timelineItemsByYear[index].timelineItems.push(timelineItem);
  });

  const timelineItemIcons = {
    achievement: Object.keys(achievementTypes).reduce(
      (achievementIcons, achievementType) => {
        const type = achievementType as Achievement['type'];
        const icon = achievementTypes[type].icon;
        achievementIcons[achievementType] = icon;
        return achievementIcons;
      },
      {} as Record<Achievement['type'], IconProp>
    ),
    'calendar-event': ['far', 'calendar-days'],
    course: 'book' as const,
    'individual-achievement': 'star' as const,
    path: 'tasks' as const
  };

  const timelineItemTypes = {
    achievement: Object.keys(achievementTypes).reduce(
      (timelineInfo, achievementType: Achievement['type']) => {
        const { timelineTitle } = achievementTypes[achievementType];
        timelineInfo[achievementType] = timelineTitle;
        return timelineInfo;
      },
      {} as Record<string, string>
    ),
    'calendar-event': messages.eventCalendarEvent,
    course: messages.profileTimelineCourseCompleted,
    'individual-achievement': messages.individualAchievementCompleted,
    path: messages.profileTimelineLearningPathCompleted
  };

  const profileBasePath = isUserPage ? `/users/${targetUserId}` : '/profile';

  return (
    <div className="timeline">
      <Box
        loading={loading}
        padding={0}
        style={{ height: '100%' }}
        title={messages.profileTimeline}
        titleTooltip={
          isUserPage && !loggedInUser?.roles?.includes('superadmin')
            ? messages.profileTimelineInfo
            : undefined
        }
      >
        <div className="content">
          {timelineItemsByYear.length ? (
            <div>
              {timelineItemsByYear.map((item, itemIndex) => (
                <div className="items" key={item.year}>
                  <div className="year">
                    <div className="year-item">{item.year}</div>
                  </div>
                  {item.timelineItems.map((timelineItem, i) => (
                    <ProfileSummaryTimelineItem
                      key={i}
                      comment={
                        timelineItem.type === 'achievement'
                          ? timelineItem.comments
                          : undefined
                      }
                      category="success-light"
                      date={timelineItem.completionDate as string}
                      icon={
                        timelineItem.type === 'achievement'
                          ? timelineItemIcons[
                              timelineItem.type as Achievement['type']
                            ][timelineItem.achievement?.type]
                          : timelineItemIcons[timelineItem.type]
                      }
                      picture={
                        timelineItem.type === 'course'
                          ? timelineItem.course?.picture
                          : undefined
                      }
                      picturePosition={
                        timelineItem.type === 'course'
                          ? timelineItem.course?.picturePosition
                          : undefined
                      }
                      title={
                        !isUserPage && timelineItem.type === 'course'
                          ? timelineItem.course?.languageVersions?.[
                              timelineItem.courseLanguageVersion
                            ]?.courseTitle || timelineItem.title
                          : timelineItem.title
                      }
                      titleHref={
                        timelineItem.type === 'achievement'
                          ? `${profileBasePath}/achievements`
                          : timelineItem.type === 'calendar-event'
                          ? `/calendar/${timelineItem.calendarEvent.id}`
                          : timelineItem.type === 'course' &&
                            timelineItem.course
                          ? `${profileBasePath}/courses/${timelineItem.course?.id}`
                          : timelineItem.type === 'path'
                          ? `${profileBasePath}/paths/${timelineItem.path?.id}`
                          : ''
                      }
                      type={
                        timelineItem.type === 'achievement'
                          ? timelineItemTypes[timelineItem.type][
                              timelineItem.achievement?.type
                            ]
                          : timelineItemTypes[timelineItem.type]
                      }
                    />
                  ))}

                  {itemIndex === timelineItemsByYear.length - 1 &&
                  totalNumberOfItems > NUMBER_OF_ITEMS_LIMIT ? (
                    <ProfileSummaryTimelineToggle
                      isExpanded={showAll}
                      numberOfAdditionalItems={
                        totalNumberOfItems - NUMBER_OF_ITEMS_LIMIT
                      }
                      onClick={() => setShowAll(!showAll)}
                    />
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          {!timelineItemsByYear?.length ? (
            <EmptyIndicator
              icon={['far', 'calendar-alt']}
              title={messages.profileTimelineEmpty}
            />
          ) : null}
        </div>
      </Box>

      <style jsx>{`
        .timeline {
          height: 100%;
        }
        .content {
          padding: 10px 15px 40px;
        }
        .items {
          margin-top: 50px;
        }
        .items:first-child {
          margin-top: 20px;
        }
        .year {
          margin: 0px 0 20px;
          text-align: left;
        }
        .year-item {
          color: ${theme.textColor};
          display: inline-block;
          font-size: 1.6rem;
          font-weight: 700;
          line-height: 1;
        }
        .actions {
          font-size: 1.6rem;
          margin-top: 10px;
          padding: 15px;
          text-align: center;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .content {
            padding: 10px 20px 40px;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfileSummaryTimeline;
